import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Button } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import fcmNotificationService from '../services/fcmNotificationService';
import './NotificationPermissionModal.scss';

// Shown by App's NotificationModalTrigger, which only opens it when the
// browser supports web push and permission is still undecided.
const NotificationPermissionModal = ({ visible, onClose, onComplete }) => {
  // Coaches and clients get different pushes; say which ones.
  const isCoach = useSelector(
    (s) => !!s.auth.user && s.auth.user.role !== 'client'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const handleEnableNotifications = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Request permission directly from the button click context
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        if (permission === 'denied') {
          setError(
            'Notifications were blocked. Please enable them in your browser settings (lock icon → Site settings → Notifications) and refresh the page.'
          );
        } else {
          setError(
            'Notification permission was not granted. Please try again.'
          );
        }
        setIsLoading(false);
        return;
      }

      // Now initialize FCM since permission is granted
      const success = await fcmNotificationService.initialize();

      if (success) {
        // Auto-save default notification preferences (push: true, email: true)
        await fcmNotificationService.updateNotificationPreferences({
          push: true,
          email: true,
        });
        setSuccessMessage('Notifications enabled!');

        // Setup foreground message listener
        fcmNotificationService.setupForegroundMessageListener(() => {});

        // Close modal after a delay
        setTimeout(() => {
          onComplete(true);
          onClose();
        }, 2000);
      } else {
        setError(
          'Failed to enable notifications. Please check your browser settings.'
        );
      }
    } catch (err) {
      console.error('❌ Error enabling notifications:', err);
      setError('An error occurred while enabling notifications.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete(false);
    onClose();
  };

  return (
    <Modal
      visible={visible}
      onCancel={handleSkip}
      footer={null}
      closable={false}
      centered
      className='notification-permission-modal'
    >
      <div className='notification-modal-content'>
        <div className='notification-icon'>
          <BellOutlined />
        </div>

        <h2>Stay Connected!</h2>
        <p className='notification-description'>
          Enable push notifications to receive important updates, messages, and
          reminders.
        </p>

        {error && <div className='notification-error'>{error}</div>}

        {successMessage && (
          <div className='notification-success'>{successMessage}</div>
        )}

        <div className='notification-features'>
          <div className='feature-item'>
            <span className='feature-icon'>💬</span>
            <span>
              {isCoach
                ? 'Messages from your clients'
                : 'Chat messages from your trainer'}
            </span>
          </div>
          <div className='feature-item'>
            <span className='feature-icon'>📅</span>
            <span>
              {isCoach
                ? 'Check-ins and workouts your clients log'
                : 'Workout and meal plan updates'}
            </span>
          </div>
          <div className='feature-item'>
            <span className='feature-icon'>🎯</span>
            <span>Important reminders and notifications</span>
          </div>
        </div>

        <div className='notification-actions'>
          <Button
            type='default'
            onClick={handleSkip}
            disabled={isLoading}
            className='skip-button'
          >
            Skip for now
          </Button>
          <Button
            type='primary'
            onClick={handleEnableNotifications}
            loading={isLoading}
            className='enable-button'
          >
            Enable Notifications
          </Button>
        </div>

        <p className='notification-hint'>
          You can change this later in your settings.
        </p>
      </div>
    </Modal>
  );
};

export default NotificationPermissionModal;
